import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { UserPlusIcon, CircleNotchIcon, WarningCircleIcon } from "@phosphor-icons/react";
import { passwordIssue, PASSWORD_RULE } from "@/lib/password";

const LOGO = "/soverenx-logo.jpg";
const inputCls = "h-11 rounded-sm bg-zinc-900 border-zinc-800 font-mono text-sm text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-1 focus-visible:ring-cyan-500";

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null); // null loading
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invites/${token}`)
      .then(({ data }) => setInfo(data))
      .catch(() => setInfo({ valid: false }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    const pwIssue = passwordIssue(password);
    if (pwIssue) { toast.error(pwIssue); return; }
    setBusy(true);
    try {
      await api.post(`/invites/${token}/accept`, { name, password });
      toast.success("Account created — please sign in");
      navigate("/login");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, "Could not create account"));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 px-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src={LOGO} alt="SoverenX Systems" className="h-14 object-contain" />
        </div>
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 backdrop-blur-xl p-7" data-testid="invite-card">
          {info === null && (
            <div className="flex justify-center py-8"><CircleNotchIcon className="h-6 w-6 animate-spin text-cyan-500" /></div>
          )}
          {info && !info.valid && (
            <div className="text-center py-6" data-testid="invite-invalid">
              <WarningCircleIcon className="mx-auto h-10 w-10 text-rose-400" />
              <h1 className="font-head text-lg text-zinc-100 mt-3">Invite unavailable</h1>
              <p className="text-sm text-zinc-500 mt-1">
                {info.used ? "This invite has already been used." : info.expired ? "This invite has expired." : "This invite link is not valid."}
              </p>
            </div>
          )}
          {info && info.valid && (
            <form onSubmit={submit} className="space-y-5" data-testid="invite-form">
              <div>
                <div className="flex items-center gap-2 text-cyan-400 mb-1">
                  <UserPlusIcon className="h-5 w-5" />
                  <h1 className="font-head text-xl font-semibold text-zinc-100">Create your account</h1>
                </div>
                <p className="text-sm text-zinc-500">Invited as <span className="text-zinc-300">{info.email}</span> ({info.role}).</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-zinc-400">Full name</label>
                <Input data-testid="invite-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Engineer" className={inputCls} />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-zinc-400">Password</label>
                <Input data-testid="invite-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 8 chars, 1 uppercase, 1 special" className={inputCls} />
                <p className="text-[11px] text-zinc-600">{PASSWORD_RULE}</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs text-zinc-400">Confirm password</label>
                <Input data-testid="invite-confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat password" className={inputCls} />
              </div>
              <button data-testid="invite-submit" disabled={busy}
                className="flex w-full items-center justify-center gap-2 rounded-sm bg-cyan-500 hover:bg-cyan-400 py-2.5 text-sm font-semibold text-zinc-950 transition-colors duration-150 disabled:opacity-60">
                {busy ? <CircleNotchIcon className="h-4 w-4 animate-spin" /> : "Create account"}
              </button>
              <p className="text-xs text-zinc-600 text-center">You'll set up 2FA on your first sign-in.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
